import { Quaternion, Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import type { CollisionWorld } from '../world/CollisionWorld';
import { findApproachingEdge, findCrossedEdge, type Room } from '../world/Room';
import type { Surface, SurfaceEdge } from '../world/Surface';
import {
  clamp,
  projectOnPlane,
  quaternionFromBasis,
  rightFromForwardUp,
  smootherstep,
  smoothstep,
  wrapAngle,
} from '../utils/math';
import { LocomotionStateMachine, type LocomotionState } from './LocomotionStateMachine';
import { Health } from '../combat/Health';
import { accelerateTowards, desiredDirection, turnToward } from './SurfaceMovement';
import {
  applyDamping,
  applyThrust,
  findGrabCandidate,
  findLandingCandidate,
  predictLanding,
} from './FreeFlight';
import { blendFrames, planLanding, transitionForward, type LandingPlan } from './Landing';

export interface RobotCommand {
  /** Strafe and forward input in the range [-1, 1]. */
  move: { x: number; y: number };
  /**
   * Tracked head lean, as a small movement request in [-1, 1]. `x` is the
   * lateral lean, `y` the vertical lean and `z` the forward/back lean. It
   * swivels the body within a bounded offset and never drives the body's
   * heading, so the robot leans without turning.
   */
  headShift?: { x: number; y?: number; z: number };
  /**
   * How far a full head lean swings the body, metres.
   *
   * The game raises it during bullet time, so the same lean swings further
   * without the mapping becoming non-linear. Omitted, the balance default.
   */
  swayRange?: number;
  jumpPressed: boolean;
  crouchHeld: boolean;
  /** Hold to move slowly and silently, with the body close to the surface. */
  sneakHeld?: boolean;
  grabHeld: boolean;
  /** World-space direction the camera is looking along. */
  viewForward: Vector3;
  /** World-space direction the camera considers "right". */
  viewRight: Vector3;
  /**
   * World-space direction from the robot to the reticle target. A jump leaves
   * along this direction instead of the surface normal, so pressing jump sends
   * the robot toward whatever the player is aiming at.
   */
  jumpDirection: Vector3;
  /**
   * Multiplier on the body's physics, 1 normally and `bulletTime.scale` while
   * the world is slowed. Movement, turning, jumping, blends and landings all
   * run on this slowed clock; the head-led dodge deliberately does not, which
   * is what makes the lean the speed advantage during bullet time.
   */
  timeScale?: number;
}

interface SurfaceTransition {
  toSurface: Surface;
  fromUp: Vector3;
  toUp: Vector3;
  fromForward: Vector3;
  toForward: Vector3;
  startPosition: Vector3;
  /** The point the body settles on, on the new surface. */
  targetPosition: Vector3;
  offset: Vector3;
  elapsed: number;
  duration: number;
}

interface LandingRuntime {
  plan: LandingPlan;
  fromUp: Vector3;
  fromForward: Vector3;
  startPosition: Vector3;
  /** Where the body settles, pulled slightly into the surface by the magnet. */
  targetPosition: Vector3;
  elapsed: number;
  duration: number;
}

const scratchDirection = new Vector3();
const scratchTarget = new Vector3();
const scratchImpulse = new Vector3();
const scratchDodge = new Vector3();
const scratchTangent = new Vector3();
const scratchRight = new Vector3();
const scratchBack = new Vector3();
const scratchUp = new Vector3();
const scratchForward = new Vector3();
const scratchQuat = new Quaternion();
const scratchHeading = new Vector3();
const scratchAttach = new Vector3();
const scratchLaunch = new Vector3();
const scratchHeadShift = new Vector3();
/** The head-led sway target and its per-frame delta. */
const scratchSway = new Vector3();
/** Where a step was clipped at the rim of an opening, and which way is into it. */
const scratchHolePoint = { u: 0, v: 0, intoU: 0, intoV: 0 };
const scratchHoleDirection = new Vector3();
const scratchObstacleBefore = new Vector3();
const scratchObstaclePush = new Vector3();
const scratchObstacleLocal = { u: 0, v: 0 };
const scratchView = new Vector3();
const scratchEdgeClamp = { u: 0, v: 0, intoU: 0, intoV: 0 };

/**
 * True when the frame carries movement input, in any direction.
 *
 * The step-across scheme runs on this, and it reads the *input*, not the
 * view: any held movement (W/S, A/D, or a diagonal) crosses a seam, so a wall
 * or ceiling can be walked onto head-on as well as sideways. What it still
 * refuses is a body with no input at all — coasting momentum must never carry
 * the robot onto the next surface, however fast it arrives, so the step is
 * always the player's own.
 */
function isStepInput(move: { x: number; y: number }): boolean {
  return Math.max(Math.abs(move.x), Math.abs(move.y)) >= BALANCE.transition.stepMin;
}

/**
 * Stop a step at a seam, the way a step is stopped at the rim of an opening.
 *
 * The local coordinate is pulled back to the seam and `intoU`/`intoV` reports
 * the unit local direction that points across it, so the caller can cancel only
 * the motion into the edge: a diagonal step slides along the seam.
 */
function clampAtEdge(
  edge: SurfaceEdge,
  u: number,
  v: number,
  out: { u: number; v: number; intoU: number; intoV: number },
): void {
  const sign = edge.sign >= 0 ? 1 : -1;
  const near = edge.value - sign * BALANCE.player.holeEdgeMargin;
  if (edge.axis === 'u') {
    out.u = near;
    out.v = v;
    out.intoU = sign;
    out.intoV = 0;
  } else {
    out.u = u;
    out.v = near;
    out.intoU = 0;
    out.intoV = sign;
  }
}

/**
 * The player character: a quadruped whose feet are magnetically attached to
 * whatever surface it is standing on.
 *
 * The robot never assumes a world "up": while attached, its up vector is the
 * normal of the current surface, and in free flight the last known up is kept.
 */
export class Robot {
  readonly position = new Vector3();
  readonly velocity = new Vector3();
  readonly up = new Vector3(0, 1, 0);
  readonly forward = new Vector3(0, 0, -1);
  readonly right = new Vector3(1, 0, 0);
  readonly bodyQuaternion = new Quaternion();

  /** The surface the robot is attached to, or null while free-floating. */
  surface: Surface | null = null;
  /** Heading of the body within the current surface frame (radians, + = right). */
  heading = 0;

  /** The player's health pool. The drones use the same implementation. */
  readonly health = new Health(BALANCE.player.maxHealth);
  /** Multiplier on corner-transition duration; higher is faster. */
  transitionSpeed = 1;
  /**
   * Step-across wall climbs.
   *
   * On, a seam between two surfaces is crossed by any held movement — walking
   * straight at it as well as strafing across it: the view direction survives
   * the change, so the world rolls about the aim axis. Only a body with no
   * movement input is stopped at the seam, like the rim of an opening. Off
   * restores the old scheme, where walking at a corner climbs it.
   */
  sidewaysTransitions = true;
  /** Speed at the last surface contact, used for landing feedback. */
  landingImpactSpeed = 0;

  private readonly stateMachine = new LocomotionStateMachine();
  private transition: SurfaceTransition | null = null;
  private landing: LandingRuntime | null = null;
  private localU = 0;
  private localV = 0;
  private coyoteTimer = 0;
  private jumpBufferTimer = 0;
  private transitionCooldown = 0;
  private transitionCountValue = 0;
  private charge = 0;

  /** The mid-air reorientation plan; see `updateFlightOrientation`. */
  private readonly flightStartQuaternion = new Quaternion();
  private readonly flightTargetQuaternion = new Quaternion();
  private readonly flightTargetUp = new Vector3(0, 1, 0);
  private readonly flightTargetForward = new Vector3(0, 0, -1);
  private flightTargetSurface: Surface | null = null;
  private flightPlanElapsed = 0;
  private flightPlanDuration = 0;

  private readonly previousQuaternion = new Quaternion();
  private angularSpeedValue = 0;
  private turnRateValue = 0;
  /** The height above the surface the body is currently held at. */
  private attachHeightValue: number = BALANCE.player.attachHeight;
  private attachHeightVelocity = 0;
  /** Smoothed sneak pose, 0 (standing) to 1 (crouched). */
  private sneakAmountValue = 0;
  /** Smoothed vertical dodge offset along the surface normal, metres. */
  private verticalDodgeValue = 0;
  /**
   * The head-led sway, as an offset from the walked position.
   *
   * On a surface it is the local u/v offset; in flight it is the world-space
   * offset. It is never folded into the walked position, so releasing the lean
   * always brings the body home instead of leaving it wherever it drifted.
   */
  private swayU = 0;
  private swayV = 0;
  /** The sway range in force (the window widens it), for the rig's tilt. */
  private swayRangeValue: number = BALANCE.headTracking.swayRange;
  private readonly swayOffset = new Vector3();

  constructor(
    private readonly room: Room,
    private readonly collision: CollisionWorld,
  ) {}

  get state(): LocomotionState {
    return this.stateMachine.state;
  }

  get isAlive(): boolean {
    return !this.health.isDestroyed;
  }

  /** The remaining hull, from 0 to the maximum. */
  get hull(): number {
    return this.health.health;
  }

  get chargeAmount(): number {
    return this.charge;
  }

  /** Total duration of the corner blend currently in progress, seconds. */
  get transitionBlendDuration(): number {
    return this.transition?.duration ?? 0;
  }

  /**
   * How many surface blends the body has started.
   *
   * Monotonic, so a consumer that samples it once per frame can spot the exact
   * frame a step began without polling the surface id. The camera rig uses it
   * to pin the view plane to the surface the player is leaving.
   */
  get transitionCount(): number {
    return this.transitionCountValue;
  }

  /** Seconds of coyote grace left, seconds. Exposed for tests and tooling. */
  get coyoteRemaining(): number {
    return Math.max(0, this.coyoteTimer);
  }

  /** Seconds a buffered jump press is still valid for, seconds. */
  get jumpBufferRemaining(): number {
    return Math.max(0, this.jumpBufferTimer);
  }

  /** True while a corner blend is running. */
  get isTransitioning(): boolean {
    return this.transition !== null;
  }

  /** How fast the body is turning, radians per second. */
  get angularSpeed(): number {
    return this.angularSpeedValue;
  }

  /** How fast the body is turning on the surface, radians per second. */
  get turnRate(): number {
    return this.turnRateValue;
  }

  /** The height above the surface the body is held at right now, metres. */
  get attachHeight(): number {
    return this.attachHeightValue;
  }

  /** How far the body has settled into the sneak pose, 0 to 1. */
  get sneakAmount(): number {
    return this.sneakAmountValue;
  }

  /** The current vertical bullet-time dodge offset, metres. */
  get verticalDodge(): number {
    return this.verticalDodgeValue;
  }

  /** How far the head lean currently has the body swivelled, metres. */
  get swayDistance(): number {
    return this.surface ? Math.hypot(this.swayU, this.swayV) : this.swayOffset.length();
  }

  /** The sway range the last command asked for, metres (the swivel cap). */
  get swayRange(): number {
    return this.swayRangeValue;
  }

  /**
   * The dodge displacement in world metres, for the lean lock.
   *
   * The body — and so the first-person eye — moves by this as well as by the
   * tracked viewer offset, so the lock has to cancel both or the aim still
   * slides as the body swivels out.
   */
  swayWorldOffset(out: Vector3): Vector3 {
    if (this.surface) {
      return out
        .copy(this.surface.right)
        .multiplyScalar(this.swayU)
        .addScaledVector(this.surface.forward, this.swayV);
    }
    return out.copy(this.swayOffset);
  }

  /**
   * The lean into the dodge, for the rig: `roll` for a lateral sway and
   * `pitch` for a forward/back one, both radians and bounded by
   * `headTracking.dodgeTilt`. It is the visible half of the Matrix dodge — the
   * body tilts out of the bolt path while the view stays on the target.
   */
  get dodgeLean(): { roll: number; pitch: number } {
    const maxTilt = BALANCE.headTracking.dodgeTilt;
    let lateral: number;
    let forward: number;
    if (this.surface) {
      lateral = this.swayU;
      forward = this.swayV;
    } else {
      scratchDodge.copy(this.swayOffset);
      lateral = scratchDodge.dot(this.right);
      forward = scratchDodge.dot(this.forward);
    }
    const scale = maxTilt / Math.max(1e-3, this.swayRangeValue);
    return {
      roll: clamp(lateral * scale, -maxTilt, maxTilt),
      // A forward lean reads as bending *back*, away from the threat.
      pitch: clamp(-forward * scale, -maxTilt, maxTilt),
    };
  }

  /** The surface the body is currently turning towards in mid air. */
  get flightTarget(): Surface | null {
    return this.flightTargetSurface;
  }

  /** Place the robot on a surface at the given local coordinates. */
  placeOnSurface(surface: Surface, u: number, v: number, heading = 0): void {
    this.surface = surface;
    this.localU = u;
    this.localV = v;
    this.heading = heading;
    this.up.copy(surface.normal);
    this.forward.copy(this.headingForward(surface, heading));
    this.right.copy(rightFromForwardUp(this.forward, this.up, scratchRight));
    this.position.copy(surface.toWorld(u, v, BALANCE.player.attachHeight, scratchTarget));
    this.velocity.set(0, 0, 0);
    this.stateMachine.reset();
    this.transition = null;
    this.landing = null;
    this.landingImpactSpeed = 0;
    this.coyoteTimer = 0;
    this.jumpBufferTimer = 0;
    this.transitionCooldown = 0;
    this.charge = 0;
    this.flightTargetSurface = null;
    this.flightPlanElapsed = 0;
    this.flightPlanDuration = 0;
    this.angularSpeedValue = 0;
    this.turnRateValue = 0;
    this.attachHeightValue = BALANCE.player.attachHeight;
    this.attachHeightVelocity = 0;
    this.sneakAmountValue = 0;
    this.verticalDodgeValue = 0;
    this.clearSway();
    this.previousQuaternion.copy(this.bodyQuaternion);
    this.updateBodyQuaternion();
  }

  takeDamage(amount: number): void {
    this.health.takeDamage(amount);
  }

  /**
   * Shove the body along a world direction, m/s.
   *
   * A bolt hit knocks the body back: on a surface the per-frame walk braking
   * turns this into a slide, and in free flight it simply carries, so a hit
   * floats the body off its heading. Runs on the body clock, so in bullet time
   * the knock plays out in slow motion with everything else.
   */
  applyImpulse(direction: Vector3, magnitude: number): void {
    if (magnitude === 0) return;
    scratchImpulse.copy(direction);
    if (scratchImpulse.lengthSq() < 1e-8) return;
    this.velocity.addScaledVector(scratchImpulse.normalize(), magnitude);
  }

  reset(surface: Surface, u: number, v: number, heading = 0): void {
    this.health.reset();
    this.placeOnSurface(surface, u, v, heading);
  }

  update(dt: number, command: RobotCommand): void {
    // Bullet time slows the body's physics but not the head-led dodge: the
    // lean stays on the real clock, which is the whole speed advantage.
    const bodyDt = dt * (command.timeScale ?? 1);
    const headDt = dt;

    this.previousQuaternion.copy(this.bodyQuaternion);
    if (this.transitionCooldown > 0) this.transitionCooldown -= bodyDt;
    if (this.coyoteTimer > 0) this.coyoteTimer -= bodyDt;
    // A jump press is remembered. The buffer only decays while the body is
    // in a settled state, so a press that arrives during a corner blend or a
    // landing survives until the body can act on it.
    if (!this.landing && !this.transition && this.jumpBufferTimer > 0) {
      this.jumpBufferTimer -= bodyDt;
    }
    if (command.jumpPressed) this.jumpBufferTimer = BALANCE.player.jumpBufferTime;

    // The posture follows the input smoothly so entering or leaving a sneak
    // does not snap the body or the camera down.
    const sneakTarget = command.sneakHeld ? 1 : 0;
    this.sneakAmountValue +=
      (sneakTarget - this.sneakAmountValue) *
      (1 - Math.exp(-BALANCE.player.sneakBlendRate * bodyDt));

    if (this.transition) {
      this.updateTransition(bodyDt);
      return;
    }
    if (this.landing) {
      this.updateLanding(bodyDt, command);
      return;
    }

    switch (this.stateMachine.state) {
      case 'SURFACE':
        this.updateSurface(bodyDt, headDt, command);
        break;
      case 'FREE_FLIGHT':
        this.updateFreeFlight(bodyDt, headDt, command);
        break;
      case 'LANDING':
        // A landing always resolves within its own update.
        break;
    }

    // Measured on real time: this is how fast the world appears to turn, so
    // it must shrink with the slowed body rather than keep its normal size.
    this.updateAngularSpeed(dt);
  }

  /**
   * A spring and damper along the surface normal.
   *
   * The magnet holds the body at `attachHeight`, but it gives a little: a
   * landing pulls the body in and lets it settle rather than snapping to the
   * surface. The give is bounded by `attachCompliance`.
   */
  private springAttachHeight(dt: number, target: number): number {
    this.attachHeightVelocity +=
      (target - this.attachHeightValue) * BALANCE.player.attachStiffness * dt;
    this.attachHeightVelocity *= Math.exp(-BALANCE.player.attachDamping * dt);
    this.attachHeightValue += this.attachHeightVelocity * dt;
    const limit = BALANCE.player.attachCompliance;
    this.attachHeightValue = clamp(this.attachHeightValue, target - limit, target + limit);
    return this.attachHeightValue;
  }

  /** Track how fast the body is turning, for the camera's FOV kick. */
  private updateAngularSpeed(dt: number): void {
    if (dt <= 0) return;
    // The body's forward can stay put while the body rolls about it, so the
    // rate is measured from the whole orientation.
    const cosine = Math.min(1, Math.abs(this.previousQuaternion.dot(this.bodyQuaternion)));
    const turned = 2 * Math.acos(cosine);
    const instant = turned / dt;
    // Smoothed, so a single noisy frame does not kick the field of view.
    this.angularSpeedValue += (instant - this.angularSpeedValue) * 0.2;
  }

  // ---------------------------------------------------------------- surface

  private updateSurface(dt: number, headDt: number, command: RobotCommand): void {
    const surface = this.surface;
    if (!surface || !surface.magnetic) {
      // A non-magnetic surface cannot hold the robot. It drifts past, which is
      // what a gap in the hull looks like (see `docs/GDD.md` §4).
      this.detachFromSurface();
      return;
    }

    const direction = desiredDirection(
      command.move,
      command.viewForward,
      command.viewRight,
      surface.normal,
      scratchDirection,
    );

    const speed =
      BALANCE.player.walkSpeed * (1 - this.sneakAmountValue * (1 - BALANCE.player.sneakSpeedScale));
    scratchTarget.copy(direction).multiplyScalar(speed);

    const hasInput = command.move.x !== 0 || command.move.y !== 0;
    const rate = hasInput ? BALANCE.player.walkAcceleration : BALANCE.player.walkBraking;
    accelerateTowards(this.velocity, scratchTarget, rate * dt, this.velocity);

    // Keep the velocity in the surface plane.
    projectOnPlane(this.velocity, surface.normal, this.velocity);

    // Advance within the surface frame.
    const previousU = this.localU;
    const previousV = this.localV;
    this.localU += this.velocity.dot(surface.right) * dt;
    this.localV += this.velocity.dot(surface.forward) * dt;

    // A tracked head lean swivels the body instead of walking it: the offset
    // follows the lean from centre, is bounded by the sway range, and comes
    // home when the lean does, so a dodge can never strand the player away
    // from their path. The swing runs on the tracker's own clock, which is the
    // one control bullet time leaves at full speed.
    this.applySurfaceSway(command, surface, headDt);

    // Doorways are openings with no metal, so the magnet holds nothing there:
    // the step is clipped at the rim and the body stops the way it would at a
    // wall, instead of gliding over the hole on thin air. A body that is
    // already over an opening is not held at all (the gap rule), which is the
    // fallback for a placement the walk never made. The doorway itself is
    // entered by jumping off the wall and flying through it.
    const rim = surface.clampOutOfHoles(
      previousU,
      previousV,
      this.localU,
      this.localV,
      BALANCE.player.holeEdgeMargin,
      scratchHolePoint,
    );
    if (rim) {
      this.localU = rim.u;
      this.localV = rim.v;
      // Cancel only the motion into the opening, so a diagonal step slides
      // along the rim instead of stopping dead against it.
      const speedInto =
        this.velocity.dot(surface.right) * rim.intoU +
        this.velocity.dot(surface.forward) * rim.intoV;
      if (speedInto > 0) {
        scratchHoleDirection
          .copy(surface.right)
          .multiplyScalar(rim.intoU)
          .addScaledVector(surface.forward, rim.intoV);
        this.velocity.addScaledVector(scratchHoleDirection, -speedInto);
      }
    } else if (surface.containsHole(this.localU, this.localV)) {
      this.detachFromSurface();
      return;
    }

    // Turn the body towards the movement input. A head lean alone must not
    // spin the robot, which is what makes the dodge read as a lean.
    const hasMoveInput = command.move.x !== 0 || command.move.y !== 0;
    if (hasMoveInput && this.velocity.lengthSq() > 0.04) {
      const desiredHeading = surface.headingOf(this.velocity, scratchHeading);
      const previousHeading = this.heading;
      this.heading = turnToward(this.heading, desiredHeading, BALANCE.player.turnRate, dt);
      const turn = wrapAngle(this.heading - previousHeading) / Math.max(1e-4, dt);
      this.turnRateValue += (turn - this.turnRateValue) * 0.25;
    } else {
      this.turnRateValue *= 0.9;
    }

    this.up.copy(surface.normal);
    this.forward.copy(this.headingForward(surface, this.heading));
    this.updateBodyQuaternion();

    // Keep the body inside the chamber. The local coordinates are allowed to
    // run past the edge (they are the integration state and decide when a
    // transition triggers), but the position never leaves the room.
    const clampedU = clamp(this.localU, surface.uMin, surface.uMax);
    const clampedV = clamp(this.localV, surface.vMin, surface.vMax);
    const height = this.springAttachHeight(dt, BALANCE.player.attachHeight);
    // Vertical head movement during bullet time shifts the body along the
    // surface normal, so ducking under a bolt is a real dodge, not just a
    // camera offset. The offset is capped and settles quickly, on the head
    // tracker's clock, so ducking is not slowed with the world either.
    const verticalTarget =
      clamp(command.headShift?.y ?? 0, -1, 1) * BALANCE.bulletTime.shiftVerticalRange;
    this.verticalDodgeValue +=
      (verticalTarget - this.verticalDodgeValue) *
      (1 - Math.exp(-BALANCE.bulletTime.shiftVerticalDamping * headDt));
    const sneakDrop = this.sneakAmountValue * BALANCE.player.sneakCrouchDrop;
    this.position.copy(
      surface.toWorld(
        clampedU,
        clampedV,
        height - sneakDrop + this.verticalDodgeValue,
        scratchTarget,
      ),
    );

    // Crates and consoles are solid: the magnet does not walk through them.
    // The push stays in the surface plane, so the body slides along the box
    // instead of being shoved off its own floor.
    scratchObstacleBefore.copy(this.position);
    if (this.collision.resolveObstacles(this.position, BALANCE.player.bodyRadius, surface.normal)) {
      scratchObstaclePush.copy(this.position).sub(scratchObstacleBefore);
      if (scratchObstaclePush.lengthSq() > 1e-12) {
        scratchObstaclePush.normalize();
        // Cancel the motion into the box (and only that), so the body slides.
        const into = this.velocity.dot(scratchObstaclePush);
        if (into < 0) this.velocity.addScaledVector(scratchObstaclePush, -into);
        surface.toLocal(this.position, scratchObstacleLocal);
        this.localU = clamp(scratchObstacleLocal.u, surface.uMin, surface.uMax);
        this.localV = clamp(scratchObstacleLocal.v, surface.vMin, surface.vMax);
        // Keep the position in step with the clamped local coordinates, so a
        // push against a crate that also touches a wall never leaves the body
        // outside the room for a frame.
        this.position.copy(
          surface.toWorld(
            this.localU,
            this.localV,
            height - sneakDrop + this.verticalDodgeValue,
            scratchTarget,
          ),
        );
      }
    }

    // Crouch charges a stronger launch.
    if (command.crouchHeld) {
      this.charge = Math.min(1, this.charge + dt / BALANCE.player.chargeTime);
    }

    if (this.jumpBufferTimer > 0) {
      this.launch(surface, command);
      return;
    }

    // Has the body walked off the edge of this surface?
    const crossed = findCrossedEdge(surface, this.localU, this.localV);

    // Or is it close enough, and moving fast enough, that the corner should
    // already be rounding? The blend starts in the band, before the body
    // reaches the seam, so the corner reads as an arc. The step-across scheme
    // has no auto-climb, so it has no blend either: only a step across the seam
    // changes surface.
    const approaching =
      crossed || this.sidewaysTransitions
        ? null
        : findApproachingEdge(
            surface,
            this.localU,
            this.localV,
            this.velocity.dot(surface.right),
            this.velocity.dot(surface.forward),
            BALANCE.transition.band,
          );
    const inBand = approaching !== null && this.velocity.length() >= BALANCE.transition.minSpeed;

    if ((crossed || inBand) && this.transitionCooldown <= 0) {
      const edge = crossed?.edge ?? approaching?.edge;
      if (edge?.kind === 'portal') {
        const target = this.room.getSurface(edge.neighbor);
        if (target && target.magnetic) {
          const along = crossed?.alongValue ?? 0;
          const span = Math.max(1e-6, edge.alongMax - edge.alongMin);
          const t = clamp((along - edge.alongMin) / span, 0, 1);
          const targetAlong =
            (edge.targetAlongMin ?? 0) +
            t * ((edge.targetAlongMax ?? 0) - (edge.targetAlongMin ?? 0));
          // `targetAxis` is the axis the target edge line lies on, so the
          // along coordinate belongs to the other axis.
          const targetValue = edge.targetValue ?? 0;
          const u = edge.targetAxis === 'u' ? targetValue : targetAlong;
          const v = edge.targetAxis === 'v' ? targetValue : targetAlong;
          this.enterPortal(target, u, v);
        } else {
          this.detachFromSurface();
        }
        return;
      }
      const neighbour = edge ? this.room.getSurface(edge.neighbor) : undefined;
      if (
        neighbour &&
        neighbour.magnetic &&
        this.sidewaysTransitions &&
        edge &&
        !isStepInput(command.move)
      ) {
        // Momentum alone never climbs a seam: with no movement input the body
        // stops at the edge the way it stops at the rim of an opening. Holding
        // a direction — W/S included — is a step, and the branch below crosses.
        clampAtEdge(edge, this.localU, this.localV, scratchEdgeClamp);
        this.localU = scratchEdgeClamp.u;
        this.localV = scratchEdgeClamp.v;
        const into =
          this.velocity.dot(surface.right) * scratchEdgeClamp.intoU +
          this.velocity.dot(surface.forward) * scratchEdgeClamp.intoV;
        if (into > 0) {
          scratchHoleDirection
            .copy(surface.right)
            .multiplyScalar(scratchEdgeClamp.intoU)
            .addScaledVector(surface.forward, scratchEdgeClamp.intoV);
          this.velocity.addScaledVector(scratchHoleDirection, -into);
        }
        this.position.copy(
          surface.toWorld(
            clamp(this.localU, surface.uMin, surface.uMax),
            clamp(this.localV, surface.vMin, surface.vMax),
            height - sneakDrop + this.verticalDodgeValue,
            scratchTarget,
          ),
        );
        return;
      }
      if (neighbour && neighbour.magnetic) {
        this.beginTransition(neighbour, command.viewForward);
      } else {
        // The neighbour cannot hold the robot: it lets go and drifts.
        this.detachFromSurface();
      }
      return;
    }
  }

  /**
   * Step through a doorway into another zone.
   *
   * The two floors are continuous, so the body keeps its speed and heading
   * instead of playing a landing animation.
   */
  private enterPortal(target: Surface, u: number, v: number): void {
    scratchForward.copy(this.forward);
    this.surface = target;
    this.localU = clamp(u, target.uMin, target.uMax);
    this.localV = clamp(v, target.vMin, target.vMax);
    this.up.copy(target.normal);
    transitionForward(scratchForward, this.up, target, this.forward);
    this.forward.normalize();
    this.right.copy(rightFromForwardUp(this.forward, this.up, scratchRight));
    this.heading = target.headingOf(this.forward, scratchHeading);
    this.attachHeightValue = BALANCE.player.attachHeight;
    this.attachHeightVelocity = 0;
    this.verticalDodgeValue = 0;
    this.clearSway();
    this.position.copy(
      target.toWorld(
        this.localU,
        this.localV,
        this.attachHeightValue - this.sneakAmountValue * BALANCE.player.sneakCrouchDrop,
        scratchTarget,
      ),
    );
    projectOnPlane(this.velocity, this.up, this.velocity);
    this.transition = null;
    this.landing = null;
    this.stateMachine.reset();
    this.transitionCooldown = 0.15;
    this.updateBodyQuaternion();
  }

  /**
   * Drop the head-led sway at once.
   *
   * A state change owns the body's position from here on (a launch, a landing,
   * a doorway), so the offset must not carry over as a teleport.
   */
  private clearSway(): void {
    this.swayU = 0;
    this.swayV = 0;
    this.swayOffset.set(0, 0, 0);
  }

  /**
   * Swing the body towards the head lean, as a bounded offset in the surface
   * plane.
   *
   * The offset is applied to the local coordinates on top of the walk, so the
   * rim clamp and the obstacle push see the swivelled position and the body can
   * never be left outside the room or inside a crate. Because the offset is
   * tracked separately, centring the head — or the bullet-time window closing,
   * which shrinks the range — unwinds it and returns the body to exactly where
   * walking put it.
   */
  private applySurfaceSway(command: RobotCommand, surface: Surface, headDt: number): void {
    const range = command.swayRange ?? BALANCE.headTracking.swayRange;
    this.swayRangeValue = range;
    const headShift = command.headShift;
    scratchHeadShift.set(0, 0, 0);
    if (headShift) {
      scratchHeadShift
        .copy(command.viewRight)
        .multiplyScalar(clamp(headShift.x, -1, 1))
        .addScaledVector(command.viewForward, clamp(headShift.z, -1, 1));
      // A full lateral lean plus the (smaller) forward component can exceed a
      // unit; the bound keeps the swing inside the range.
      if (scratchHeadShift.lengthSq() > 1) scratchHeadShift.normalize();
      projectOnPlane(scratchHeadShift, surface.normal, scratchHeadShift);
    }

    const targetU = scratchHeadShift.dot(surface.right) * range;
    const targetV = scratchHeadShift.dot(surface.forward) * range;
    if (targetU === this.swayU && targetV === this.swayV) return;

    const k = 1 - Math.exp(-BALANCE.headTracking.swayDamping * headDt);
    const deltaU = (targetU - this.swayU) * k;
    const deltaV = (targetV - this.swayV) * k;
    this.swayU += deltaU;
    this.swayV += deltaV;
    this.localU += deltaU;
    this.localV += deltaV;
  }

  private headingForward(surface: Surface, heading: number, out = new Vector3()): Vector3 {
    return out
      .copy(surface.forward)
      .multiplyScalar(Math.cos(heading))
      .addScaledVector(surface.right, Math.sin(heading))
      .normalize();
  }

  private updateBodyQuaternion(): void {
    this.right.copy(rightFromForwardUp(this.forward, this.up, scratchRight));
    scratchBack.copy(this.forward).negate();
    quaternionFromBasis(this.right, this.up, scratchBack, this.bodyQuaternion);
  }

  // ------------------------------------------------------------------ launch

  private launch(surface: Surface, command: RobotCommand): void {
    const chargeMultiplier = 1 + (BALANCE.player.chargeMultiplier - 1) * this.charge;
    const impulse = BALANCE.player.jumpImpulse * chargeMultiplier;
    this.charge = 0;
    // A press is consumed by the launch it triggers.
    this.jumpBufferTimer = 0;

    // The launch follows the reticle target, not the surface normal. A jump
    // aimed along the floor still gets a minimum lift so the feet can leave.
    scratchLaunch.copy(command.jumpDirection);
    if (scratchLaunch.lengthSq() < 1e-8) scratchLaunch.copy(surface.normal);
    const alongNormal = scratchLaunch.dot(surface.normal);
    if (alongNormal < BALANCE.player.jumpMinNormal) {
      scratchLaunch.addScaledVector(surface.normal, BALANCE.player.jumpMinNormal - alongNormal);
    }
    scratchLaunch.normalize();

    // Keep a little of the walk momentum, but the aim decides the leap.
    this.velocity
      .multiplyScalar(BALANCE.player.jumpMomentumRetention)
      .addScaledVector(scratchLaunch, impulse);

    this.surface = null;
    this.verticalDodgeValue = 0;
    this.clearSway();
    this.stateMachine.transition('FREE_FLIGHT');
  }

  /**
   * Turn the body towards the surface it is heading for while it is in mid
   * air.
   *
   * The body works out the rotation it needs to arrive square to the landing
   * surface and schedules it to finish at the moment of touchdown, so the
   * rate falls out of the distance rather than being a fixed rate. When the
   * prediction changes the plan restarts from wherever the body is now, so
   * the motion is always continuous.
   */
  private updateFlightOrientation(dt: number): void {
    const prediction = predictLanding(
      this.room,
      this.collision,
      this.position,
      this.velocity,
      BALANCE.player.landingLookAhead,
    );

    if (prediction.surface && prediction.surface !== this.flightTargetSurface) {
      this.flightTargetSurface = prediction.surface;
      this.flightStartQuaternion.copy(this.bodyQuaternion);
      this.planFlightTarget(prediction.surface);
      this.flightPlanElapsed = 0;
      const speed = Math.max(0.01, this.velocity.length());
      this.flightPlanDuration = Math.max(
        BALANCE.player.minFlightRotationTime,
        prediction.distance / speed,
      );
    }

    if (!this.flightTargetSurface) return;

    this.flightPlanElapsed += dt;
    const t = clamp(this.flightPlanElapsed / this.flightPlanDuration, 0, 1);
    const eased = smoothstep(t);

    // A quaternion slerp keeps the frame orthonormal all the way through, and
    // it survives the half turn the body needs to land on a ceiling.
    this.bodyQuaternion.slerpQuaternions(
      this.flightStartQuaternion,
      this.flightTargetQuaternion,
      eased,
    );
    this.up.set(0, 1, 0).applyQuaternion(this.bodyQuaternion);
    this.forward.set(0, 0, -1).applyQuaternion(this.bodyQuaternion);
    this.right.set(1, 0, 0).applyQuaternion(this.bodyQuaternion);
  }

  /**
   * The orientation the body wants to be in when it reaches the surface: its
   * up is the surface's normal, and it faces the way it is travelling.
   */
  private planFlightTarget(surface: Surface): void {
    this.flightTargetUp.copy(surface.normal);
    const projected = projectOnPlane(this.velocity, surface.normal, scratchTarget);
    if (projected.lengthSq() > 1e-6) {
      this.flightTargetForward.copy(projected).normalize();
    } else {
      // Travelling straight into the surface: any facing will do.
      this.flightTargetForward.copy(surface.forward);
    }
    const right = rightFromForwardUp(this.flightTargetForward, this.flightTargetUp, scratchRight);
    const back = scratchBack.copy(this.flightTargetForward).negate();
    quaternionFromBasis(right, this.flightTargetUp, back, this.flightTargetQuaternion);
  }

  /** Leave the current surface without jumping; a short coyote jump remains. */
  private detachFromSurface(): void {
    this.surface = null;
    this.coyoteTimer = BALANCE.player.coyoteTime;
    this.charge = 0;
    this.verticalDodgeValue = 0;
    this.clearSway();
    this.stateMachine.transition('FREE_FLIGHT');
  }

  /**
   * Let go of the surface at once and enter free flight, because something
   * else now owns the motion: a grapple hook that has anchored to a wall
   * reels the body in, and it cannot do that through a magnet, a corner blend
   * or a landing that is still playing out.
   *
   * Unlike `detachFromSurface` there is no coyote grace: the body is being
   * pulled, not walking off an edge, so a buffered jump must not fire while
   * the hook is reeling it in.
   */
  detachToFlight(): void {
    if (!this.stateMachine.canTransition('FREE_FLIGHT')) return;
    this.transition = null;
    this.landing = null;
    this.surface = null;
    this.charge = 0;
    this.verticalDodgeValue = 0;
    this.clearSway();
    this.coyoteTimer = 0;
    this.jumpBufferTimer = 0;
    this.stateMachine.transition('FREE_FLIGHT');
  }

  // -------------------------------------------------------------- free flight

  private updateFreeFlight(dt: number, headDt: number, command: RobotCommand): void {
    // A jump is still allowed shortly after the body left a surface, whether
    // it walked off an open edge or finished a corner transition.
    if (this.jumpBufferTimer > 0 && this.coyoteTimer > 0) {
      scratchLaunch.copy(command.jumpDirection);
      if (scratchLaunch.lengthSq() < 1e-8) scratchLaunch.copy(this.up);
      this.velocity.addScaledVector(scratchLaunch.normalize(), BALANCE.player.jumpImpulse);
      this.coyoteTimer = 0;
      this.jumpBufferTimer = 0;
    }

    applyThrust(
      this.velocity,
      command.move,
      command.viewForward,
      command.viewRight,
      BALANCE.player.thrustAcceleration,
      dt,
    );
    applyDamping(this.velocity, BALANCE.player.flightDamping, dt);

    this.position.addScaledVector(this.velocity, dt);

    // The same swivel works in free flight: the lean swings the body within a
    // bounded offset along the view axes and along the body's own up, on the
    // tracker's clock, and unwinds when the lean centres. It is a position
    // offset rather than a thrust, so it never builds speed.
    const range = command.swayRange ?? BALANCE.headTracking.swayRange;
    const headShift = command.headShift;
    scratchSway.set(0, 0, 0);
    if (headShift) {
      scratchHeadShift
        .copy(command.viewRight)
        .multiplyScalar(clamp(headShift.x, -1, 1))
        .addScaledVector(command.viewForward, clamp(headShift.z, -1, 1));
      if (scratchHeadShift.lengthSq() > 1) scratchHeadShift.normalize();
      scratchSway
        .copy(scratchHeadShift)
        .multiplyScalar(range)
        .addScaledVector(
          this.up,
          clamp(headShift.y ?? 0, -1, 1) * BALANCE.bulletTime.shiftVerticalRange,
        );
    }
    if (scratchSway.distanceToSquared(this.swayOffset) > 1e-12) {
      const k = 1 - Math.exp(-BALANCE.headTracking.swayDamping * headDt);
      scratchSway.sub(this.swayOffset).multiplyScalar(k);
      this.swayOffset.add(scratchSway);
      this.position.add(scratchSway);
    }

    this.collision.resolveObstacles(this.position, BALANCE.player.bodyRadius);
    this.constrainInsideRoom();

    this.updateFlightOrientation(dt);

    const landingSurface = findLandingCandidate(
      this.room,
      this.position,
      this.velocity,
      BALANCE.landing.contactDistance,
    );
    if (landingSurface) {
      this.beginLanding(landingSurface);
      return;
    }

    if (command.grabHeld) {
      const grabSurface = findGrabCandidate(
        this.room,
        this.position,
        BALANCE.player.grabRadius + BALANCE.player.attachHeight,
      );
      if (grabSurface) {
        this.beginLanding(grabSurface);
      }
    }
  }

  /** Keep the body inside its current zone even when it is not touching a surface. */
  private constrainInsideRoom(): void {
    this.room.constrain(this.position, this.velocity, BALANCE.player.wallClearance);
  }

  // ----------------------------------------------------------------- landing

  private beginLanding(surface: Surface): void {
    const plan = planLanding(surface, this.position, this.forward, BALANCE.player.attachHeight);

    this.landingImpactSpeed = this.velocity.length();
    // The landing pulls the magnet in: the body settles slightly into the
    // surface and the spring pushes it back out.
    const compression = Math.min(BALANCE.player.attachCompliance, this.landingImpactSpeed * 0.008);
    this.attachHeightValue = BALANCE.player.attachHeight - compression;
    this.attachHeightVelocity = 0;

    this.landing = {
      plan,
      fromUp: this.up.clone(),
      fromForward: this.forward.clone(),
      startPosition: this.position.clone(),
      // The landing pulls the magnet in: the body settles slightly into the
      // surface and the spring pushes it back out.
      targetPosition: plan.targetPosition.clone().addScaledVector(surface.normal, -compression),
      elapsed: 0,
      duration: BALANCE.landing.duration,
    };
    this.stateMachine.transition('LANDING');
  }

  private updateLanding(dt: number, command: RobotCommand): void {
    const landing = this.landing;
    if (!landing) return;
    landing.elapsed += dt;

    const t = clamp(landing.elapsed / landing.duration, 0, 1);
    const eased = smootherstep(t);

    blendFrames(
      landing.fromUp,
      landing.fromForward,
      landing.plan.surface.normal,
      landing.plan.forward,
      eased,
      scratchQuat,
      scratchUp,
      scratchForward,
    );
    this.bodyQuaternion.copy(scratchQuat);
    this.up.copy(scratchUp);
    this.forward.copy(scratchForward);
    this.right.copy(rightFromForwardUp(this.forward, this.up, scratchRight));

    this.position.lerpVectors(landing.startPosition, landing.targetPosition, eased);
    this.velocity.multiplyScalar(Math.pow(1 - BALANCE.landing.damping, dt * 60));
    // `timeout` is a safety net for a landing blend that could not finish.
    if (t >= 1 || landing.elapsed >= BALANCE.landing.timeout) {
      this.surface = landing.plan.surface;
      this.localU = landing.plan.local.u;
      this.localV = landing.plan.local.v;
      this.up.copy(landing.plan.surface.normal);
      this.forward.copy(landing.plan.forward);
      this.heading = landing.plan.surface.headingOf(landing.plan.forward, scratchHeading);
      // The body lands slightly compressed, and the spring pushes it back out.
      this.position.copy(landing.targetPosition);
      projectOnPlane(this.velocity, this.surface.normal, this.velocity);
      this.landing = null;
      this.stateMachine.transition('SURFACE');
      this.updateBodyQuaternion();

      // Skip the settle frame when the player buffered a jump.
      if (this.jumpBufferTimer > 0) {
        this.jumpBufferTimer = 0;
        this.launch(this.surface, command);
      }
    }
  }

  // -------------------------------------------------------------- transition

  private beginTransition(target: Surface, viewForward?: Vector3): void {
    this.transitionCountValue += 1;
    const duration = BALANCE.transition.duration / clamp(this.transitionSpeed, 0.2, 5);
    // In the step-across scheme the step is taken into the seam, so the view
    // already lies in the new surface's plane: the body comes out facing where
    // the player was looking, and the view keeps pointing at the same thing
    // while the world rolls about it. The legacy scheme keeps the old rule,
    // where the body's own direction decides (and a walk at a wall becomes a
    // climb).
    const preferred =
      this.sidewaysTransitions && viewForward ? scratchView.copy(viewForward) : this.forward;
    const newForward = transitionForward(preferred, this.up, target, new Vector3());

    // The direction of travel is carried over: walking into a wall becomes
    // climbing it, and a sidestep carries on along the wall, at the same speed.
    const speed = this.velocity.length();
    this.velocity.copy(newForward).multiplyScalar(speed);

    // Where does the body step onto the new surface?
    scratchTarget.copy(this.position);
    const local = target.toLocal(scratchTarget);
    const clampedU = clamp(local.u, target.uMin, target.uMax);
    const clampedV = clamp(local.v, target.vMin, target.vMax);

    this.transition = {
      toSurface: target,
      fromUp: this.up.clone(),
      toUp: target.normal.clone(),
      fromForward: this.forward.clone(),
      toForward: newForward.clone(),
      startPosition: this.position.clone(),
      // The point the body settles on, on the new surface. The blend arcs
      // from where the body is to there, so it never sinks into the surface
      // it is leaving.
      targetPosition: target.toWorld(
        clampedU,
        clampedV,
        BALANCE.player.attachHeight - this.sneakAmountValue * BALANCE.player.sneakCrouchDrop,
        new Vector3(),
      ),
      offset: new Vector3(),
      elapsed: 0,
      duration,
    };

    this.localU = clampedU;
    this.localV = clampedV;
    this.surface = target;
    this.transitionCooldown = BALANCE.transition.cooldown + duration;
  }

  /**
   * Clamp the corner slide so the body stays within the bounds of the surface
   * it is transitioning onto.
   */
  private clampTransitionOffset(transition: SurfaceTransition): void {
    const surface = transition.toSurface;
    const anchor = surface.toLocal(transition.startPosition);
    const offsetU = transition.offset.dot(surface.right);
    const offsetV = transition.offset.dot(surface.forward);

    const targetU = clamp(anchor.u + offsetU, surface.uMin, surface.uMax);
    const targetV = clamp(anchor.v + offsetV, surface.vMin, surface.vMax);
    if (targetU === anchor.u + offsetU && targetV === anchor.v + offsetV) return;

    transition.offset
      .copy(surface.right)
      .multiplyScalar(targetU - anchor.u)
      .addScaledVector(surface.forward, targetV - anchor.v);
  }

  private updateTransition(dt: number): void {
    const transition = this.transition;
    if (!transition) return;
    transition.elapsed += dt;

    const t = clamp(transition.elapsed / transition.duration, 0, 1);
    const eased = smootherstep(t);

    // Keep moving along the new surface while the body rotates around the
    // edge. The slide is clamped to the new surface so the body cannot run
    // off the far end of a wall while it is still rounding the corner.
    projectOnPlane(this.velocity, transition.toUp, scratchTangent);
    transition.offset.addScaledVector(scratchTangent, dt);
    this.clampTransitionOffset(transition);

    blendFrames(
      transition.fromUp,
      transition.fromForward,
      transition.toUp,
      transition.toForward,
      eased,
      scratchQuat,
      scratchUp,
      scratchForward,
    );
    this.bodyQuaternion.copy(scratchQuat);
    this.up.copy(scratchUp);
    this.forward.copy(scratchForward);
    this.right.copy(rightFromForwardUp(this.forward, this.up, scratchRight));

    // The body arcs from where it is to the point it will settle on, so it
    // never sinks into the surface it is leaving, and the slide along the new
    // surface is clamped so it cannot run off the far end of a wall.
    scratchAttach.lerpVectors(transition.startPosition, transition.targetPosition, eased);
    this.position.copy(scratchAttach).add(transition.offset);

    this.heading = transition.toSurface.headingOf(scratchForward, scratchHeading);

    if (t >= 1) {
      this.surface = transition.toSurface;
      this.up.copy(transition.toUp);
      this.forward.copy(transition.toForward);
      this.localU = clamp(
        transition.toSurface.toLocal(this.position).u,
        transition.toSurface.uMin,
        transition.toSurface.uMax,
      );
      this.localV = clamp(
        transition.toSurface.toLocal(this.position).v,
        transition.toSurface.vMin,
        transition.toSurface.vMax,
      );
      this.transition = null;
      this.updateBodyQuaternion();
      // The body was unattached for the length of the blend, so the usual
      // coyote grace applies here too.
      this.coyoteTimer = BALANCE.player.coyoteTime;
    }
  }

  // ------------------------------------------------------------------ helpers

  /** Feet position, i.e. the contact point used for surface tests. */
  getFeetPosition(out = new Vector3()): Vector3 {
    const height =
      this.attachHeightValue -
      this.sneakAmountValue * BALANCE.player.sneakCrouchDrop +
      this.verticalDodgeValue;
    return out.copy(this.position).addScaledVector(this.up, -height);
  }

  /** Direction the body is looking, ignoring the head offset. */
  getViewForward(out = new Vector3()): Vector3 {
    return out.copy(this.forward);
  }
}
